import { apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'

export type EmployerVerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED'
export type ApplicationStatus =
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'SHORTLISTED'
  | 'INTERVIEW_SCHEDULED'
  | 'OFFER_EXTENDED'
  | 'OFFER_ACCEPTED'
  | 'OFFER_DECLINED'
  | 'REJECTED'
  | 'WITHDRAWN'

export interface SchoolAdminInstitution {
  city?: string | null
  description?: string | null
  id: string
  logoUrl?: string | null
  name: string
  state?: string | null
  website?: string | null
}

export interface SchoolAdminProfile {
  [key: string]: unknown
  avatarUrl?: string | null
  firstName: string
  id: string
  institution?: SchoolAdminInstitution | null
  lastName: string
  phoneNumber?: string
  title?: string
}

export interface SchoolAdminDashboardStats {
  achievementScore: number
  applications: { pending: number; total: number }
  employerEngagement: { interviews: number; messages: number; total: number }
  events: { upcoming: number }
  jobs: { active: number; draft: number; total: number }
  offers: { total: number }
  students: { pendingVerification: number; total: number; verified: number }
  universityRequests: { pending: number }
}

export interface AdminStudentSummary {
  [key: string]: unknown
  _count: { applications: number; savedJobs?: number }
  achievementScore: number
  avatarUrl?: string | null
  degreeProgram?: string
  employerEngagement: { interviews: number; messages: number; total: number }
  expectedGraduation?: string
  firstName: string
  id: string
  lastName: string
  major?: string
  offers: number
  profileCompletionPercentage: number
  user: { createdAt: string; email: string }
  verificationStatus: 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED'
}

export interface EducationRecord {
  degree?: string
  description?: string
  endDate?: string | null
  gpa?: number | null
  id: string
  institution?: string
  isCurrent?: boolean
  major?: string
  minor?: string | null
  startDate?: string
}

export interface ExperienceRecord {
  company?: string
  description?: string
  employmentType?: string
  endDate?: string | null
  id: string
  isCurrent?: boolean
  isRemote?: boolean
  location?: string
  startDate?: string
  title?: string
}

export interface VolunteerRecord {
  description?: string
  endDate?: string | null
  id: string
  isCurrent?: boolean
  organization?: string
  startDate?: string
  title?: string
}

export interface AdminStudentDocument {
  createdAt: string
  id: string
  mimeType?: string
  name: string
  sizeBytes?: number
  type: string
  url: string
}

export interface AdminStudentDetail extends AdminStudentSummary {
  adminNotes?: string | null
  bio?: string
  documents?: AdminStudentDocument[]
  education?: EducationRecord[]
  engagement?: { mockInterviews: number; resumeReviews: number; workshopsAttended: number }
  experience?: ExperienceRecord[]
  gpa?: number | null
  interests?: string[]
  linkedinUrl?: string
  location?: string
  phoneNumber?: string
  portfolioUrl?: string
  preferredEmploymentTypes?: string[]
  profileCompletion?: {
    hasAcademicInfo: boolean
    hasBio: boolean
    hasContactInfo: boolean
    hasEducation: boolean
    hasLinksOrPortfolio: boolean
    hasResume: boolean
    hasSkills: boolean
    hasWorkAuthorization: boolean
    percentage: number
  }
  resumeUrl?: string
  skills?: string[],
  visaDaysRemaining?: number | null,
  visaEndDate?: string | null,
  visaStartDate?: string | null,
  volunteerEngagements?: VolunteerRecord[],
  workAuthApprovalDate?: string | null,
  workAuthApprovalStatus?: string,
  workAuthorization?: string
}

export interface AdminApplicationSummary {
  id: string
  // A job's employer link is optional (institution-posted jobs with no
  // employer, or one whose employer profile was later removed) - always
  // guard this, it's not guaranteed to be present.
  job: { employer: { companyName: string } | null; title: string }
  status: ApplicationStatus
  student: { avatarUrl?: string | null; firstName: string; id: string; lastName: string }
  submittedAt: string
}

export interface AdminEmployerSummary {
  [key: string]: unknown
  _count: { active: number; draft: number }
  avatarUrl?: string | null
  city?: string
  companyName: string
  engagement: { applications: number; interviews: number; messages: number; offers: number; studentsEngaged: number }
  id: string
  industry?: string
  rating?: { average: number | null; count: number }
  state?: string
  verificationStatus: EmployerVerificationStatus
}

export interface AdminEmployerJob {
  applicationCount: number
  createdAt: string
  employmentType?: string
  id: string
  isRemote: boolean
  location?: string
  salaryMax?: number | null
  salaryMin?: number | null
  status: string
  title: string
  viewCount: number
}

export interface AdminEmployerReview {
  comment?: string | null
  createdAt: string
  id: string
  raterRole: 'STUDENT' | 'SCHOOL_ADMIN'
  stars: number
}

export interface AdminEmployerDetail extends AdminEmployerSummary {
  companyDescription?: string
  companyWebsite?: string
  createdAt?: string
  currentlySponsoring?: string
  h1bSponsorship?: string
  jobs?: AdminEmployerJob[]
  recentInteractions: {
    id: string
    job: { title: string }
    status: ApplicationStatus
    student: { avatarUrl?: string | null; firstName: string; id: string; lastName: string }
    submittedAt: string
  }[]
  reviews?: AdminEmployerReview[]
  stats: { hires: number; interviewsConducted: number; offers: number; totalApplications: number }
  user: { email: string }
}

export interface UniversityRequest {
  [key: string]: unknown
  createdAt: string
  id: string
  schoolContactEmail?: string
  schoolName: string
  status: string
  studentEmail: string
  studentFullName: string
}

export const getSchoolAdminProfile = () => apiGet<SchoolAdminProfile>('/school-admins/me')

export const updateSchoolAdminProfile = (payload: Partial<Pick<SchoolAdminProfile, 'avatarUrl' | 'firstName' | 'lastName' | 'phoneNumber' | 'title'>>) =>
  apiPatch<SchoolAdminProfile>('/school-admins/me', payload)

export const getSchoolAdminDashboardStats = () => apiGet<SchoolAdminDashboardStats>('/school-admins/me/dashboard')

export interface UpdateInstitutionPayload {
  address?: string
  bannerUrl?: string
  city?: string
  country?: string
  description?: string
  domain?: string
  logoUrl?: string
  name?: string
  state?: string
  website?: string
}

export const updateSchoolAdminInstitution = (payload: UpdateInstitutionPayload) => apiPatch<SchoolAdminInstitution>('/school-admins/me/institution', payload)

export const listStudents = (params?: {
  graduationYear?: string
  hasApplications?: 'YES' | 'NO'
  hasEngagement?: 'YES' | 'NO'
  limit?: number
  page?: number
  search?: string
  verificationStatus?: string
}): Promise<PaginatedResult<AdminStudentSummary[]>> =>
  apiGetPaginated<AdminStudentSummary[]>('/school-admins/me/students', { params })

export const getGraduationYears = () => apiGet<number[]>('/school-admins/me/students/graduation-years')

export const getStudent = (studentId: string) => apiGet<AdminStudentDetail>(`/school-admins/me/students/${studentId}`)

export const verifyStudent = (studentId: string, isVerified = true) => apiPost<AdminStudentSummary>(`/school-admins/me/students/${studentId}/verify`, { isVerified })

export interface BulkVerifyResult {
  skipped: string[]
  updated: number
}

// Matches the backend's own per-request cap (school-admin-profile.dto.ts).
const BULK_VERIFY_BATCH_SIZE = 20

export const bulkVerifyStudents = (studentIds: string[], isVerified = true) =>
  apiPost<BulkVerifyResult>('/school-admins/me/students/bulk-verify', { isVerified, studentIds })

/** Verifies any number of students by sending them to the backend one batch
 * of BULK_VERIFY_BATCH_SIZE at a time (sequential, not parallel) so a large
 * selection never fires more than one request at once. */
export const bulkVerifyStudentsBatched = async (studentIds: string[], isVerified = true): Promise<BulkVerifyResult> => {
  const aggregate: BulkVerifyResult = { skipped: [], updated: 0 }
  for (let i = 0; i < studentIds.length; i += BULK_VERIFY_BATCH_SIZE) {
    const batch = studentIds.slice(i, i + BULK_VERIFY_BATCH_SIZE)
    const result = await bulkVerifyStudents(batch, isVerified)
    aggregate.updated += result.updated
    aggregate.skipped.push(...result.skipped)
  }
  return aggregate
}

export const updateStudentNotes = (studentId: string, notes: string) => apiPatch<{ id: string; notes: string }>(`/school-admins/me/students/${studentId}/notes`, { notes })

export const inviteStudent = (email: string) => apiPost<{ email: string }>('/school-admins/me/students/invite', { email })

export interface PendingStudentInvite {
  createdAt: string
  email: string
  id: string
}

export const listPendingInvites = () => apiGet<PendingStudentInvite[]>('/school-admins/me/students/invites')

export const listStudentApplications = (studentId: string, params?: { limit?: number; page?: number; status?: ApplicationStatus }): Promise<PaginatedResult<AdminApplicationSummary[]>> =>
  apiGetPaginated<AdminApplicationSummary[]>(`/school-admins/me/students/${studentId}/applications`, { params })

export const listApplications = (params?: { limit?: number; page?: number; status?: ApplicationStatus; studentId?: string }): Promise<PaginatedResult<AdminApplicationSummary[]>> =>
  apiGetPaginated<AdminApplicationSummary[]>('/school-admins/me/applications', { params })

export const listEmployers = (params?: { industry?: string; limit?: number; page?: number; search?: string }): Promise<PaginatedResult<AdminEmployerSummary[]>> =>
  apiGetPaginated<AdminEmployerSummary[]>('/school-admins/me/employers', { params })

export const getEmployer = (employerId: string) => apiGet<AdminEmployerDetail>(`/school-admins/me/employers/${employerId}`)

export const listUniversityRequests = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<UniversityRequest[]>> =>
  apiGetPaginated<UniversityRequest[]>('/school-admins/me/university-requests', { params })

export const registerStudentForEvent = (eventId: string, studentId: string) => apiPost<{ rsvped: boolean }>(`/school-admins/me/events/${eventId}/register-student`, { studentId })
